import { useState, useMemo, useEffect } from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from "./ui/command";
import { Input } from "./ui/input";
import { useLang } from "../i18n";
import { cn } from "../lib/utils";

function flagEmoji(cc) {
  if (!cc) return "🏳️";
  return cc
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Searchable country-code phone input. Emits an E.164 string (+905321234567)
// via onChange(e164, isValid) and keeps the national number editable.
export function PhoneInput({
  country,
  setCountry,
  national,
  setNational,
  onChange,
  testid = "phone",
  inputClassName,
}) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);

  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([lang === "tr" ? "tr" : "en"], { type: "region" });
    } catch (_) {
      return null;
    }
  }, [lang]);

  const countries = useMemo(
    () =>
      getCountries()
        .map((cc) => ({
          cc,
          name: (regionNames && regionNames.of(cc)) || cc,
          calling: getCountryCallingCode(cc),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [regionNames]
  );

  const current = countries.find((c) => c.cc === country);

  useEffect(() => {
    const parsed = national ? parsePhoneNumberFromString(national, country) : null;
    const valid = parsed ? parsed.isValid() : false;
    onChange?.(valid ? parsed.number : "", valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, national]);

  const handleNationalChange = (e) => {
    const v = e.target.value;
    const trimmed = v.trim();
    // Support pasted international numbers (+90..., 0090...)
    if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
      const normalized = trimmed.startsWith("00") ? "+" + trimmed.slice(2) : trimmed;
      const parsed = parsePhoneNumberFromString(normalized);
      if (parsed) {
        if (parsed.country) setCountry(parsed.country);
        setNational(parsed.nationalNumber);
        return;
      }
    }
    setNational(v.replace(/[^\d\s()\-/.]/g, ""));
  };

  return (
    <div className="flex gap-2" data-testid={`${testid}-wrapper`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testid}-country-btn`}
            className="flex h-14 shrink-0 items-center gap-1 rounded-xl border border-[#26422A]/60 bg-white px-3 font-semibold text-[#26422A]"
          >
            <span className="text-xl leading-none">{flagEmoji(country)}</span>
            <span className="text-sm">+{current?.calling}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(320px,90vw)] p-0" align="start">
          <Command
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <CommandInput
              placeholder={t("searchCountry")}
              data-testid={`${testid}-country-search`}
            />
            <CommandList>
              <CommandEmpty>{t("noCountryFound")}</CommandEmpty>
              {countries.map((c) => (
                <CommandItem
                  key={c.cc}
                  value={`${c.name} ${c.cc} +${c.calling}`}
                  data-testid={`country-opt-${c.cc}`}
                  onSelect={() => {
                    setCountry(c.cc);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <span className="text-xl leading-none">{flagEmoji(c.cc)}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground">+{c.calling}</span>
                  {c.cc === country && <Check className="h-4 w-4 text-[#26422A]" />}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        data-testid={`${testid}-input`}
        type="tel"
        inputMode="tel"
        value={national}
        onChange={handleNationalChange}
        placeholder="5XX XXX XX XX"
        className={cn(
          "h-14 flex-1 rounded-xl border-[#26422A]/60 bg-white text-lg font-semibold",
          inputClassName
        )}
      />
    </div>
  );
}
